<template>
  <div class="drop-container">
    <LKProgressCircular
      v-if="rootNode.loading || isLoading || filtersLoading"
      :size="50"
      overlay
      style="align-items: start; padding-top: 30vh;"
    />

    <div
      v-else-if="hasEmptyFilterMessage"
      style="padding-top: 20px;"
      class="text-md"
      v-text="emptyFilterMessage"
    />

    <div
      v-else-if="rootNode.children.length === 0"
      style="padding-top: 20px;"
      class="text-md"
      v-text="'Карт нет'"
    />

    <TreeChildren
      v-else
      :node="rootNode"
      :load-children="onLoadChildren"

      v-on="$listeners || {}"
      @open-history="onOpenEventHitory"
      @change:status="onChangeEventStatus"
      @change:expert-status="onChangeExpertStatus"
      @change:date="onChangeEventDate"
    />
  </div>
</template>

<script>
/* eslint no-param-reassign: 0 */
/**
 * @typedef {import('@/components/forms/BAteTree/utils').TreeNode} TreeNode
 * @typedef {import('@/typings/ws/WSEventUnread').WSEventUnread} WSEventUnread
 * @typedef {import('@/typings/ws/WSRoadmap').WSRoadmap} WSRoadmap
 * @typedef {import('@/typings/ws/WSRoadmap').WSRoadmapEvent} WSRoadmapEvent
 * @typedef {import('@/typings/ws/WSEventStatusChanged').WSEventStatusChanged} WSEventStatusChanged
 */


import axios from 'axios'
import { Subject, defer } from 'rxjs'
import {
  flatMap,
  retryWhen,
  delay,
  take,
} from 'rxjs/operators'

import memoize from 'lodash/memoize'
import omitBy from 'lodash/omitBy'
import uniq from 'lodash/uniq'
import difference from 'lodash/difference'
import { BaseTaskQuery, TaskTypes } from '@/api/BaseWS'
import {
  RoadmapsFeedApi,
  RoadmapEventsApi,
} from '@/api/resources'
import { LkRoadmapExpertsFormApi } from '@/api/resources/LK/Roadmap/Experts/Form'
import { RootTreeNode, IS_LAST } from '@/components/forms/BAteTree/utils'
import { RoadmapEventExpertStatuses, RoadmapEventStatuses, getRoadmapEventExpertStatusesLabel } from '@/typings/api/RoadmapEvent'

import LKProgressCircular from '@/components/lk/LKProgressCircular.vue'
import TreeChildren, { LEVELS } from './LKRoadmapsFeedTreeChildren.vue'
import { lkMapper } from '@/store/modules/lk'


const PER_PAGE = 200
const CONCURRENT_COUNT = 5
const CONCURRENT_RETRY_COUNT = 2
const CONCURRENT_RETRY_TIMEOUT = 2000
const TIMEOUT_READ = 500

const isEmptyCustom = (value) => (
  !value || (value && `${value}`.length === 0)
)

const resolverFilters = (id = 0, filters = {}) => (
  `${JSON.stringify(id)}--${JSON.stringify(filters)}`
)

const STATUS_COUNT_MAP = {
  [RoadmapEventStatuses.inProgress]: `${RoadmapEventStatuses.inProgress}_count`,
  [RoadmapEventStatuses.needsWork]: `${RoadmapEventStatuses.needsWork}_count`,
  // [RoadmapEventStatuses.needsCheck]: `${RoadmapEventStatuses.needsCheck}_count`,
  [RoadmapEventStatuses.expired]: `${RoadmapEventStatuses.expired}_count`,
  [RoadmapEventStatuses.done]: `${RoadmapEventStatuses.done}_count`,
  [RoadmapEventStatuses.confirmed]: `${RoadmapEventStatuses.done}_count`,
}

export {
  LEVELS,
  PER_PAGE,
  RootTreeNode,
}

export default {
  name: 'LKRoadmapsFeedTree',
  inject: [
    'isAdresat',
    'isCurator',
    'isCoordinator',
  ],
  components: {
    LKProgressCircular,
    TreeChildren,
  },
  props: {
    filters: {
      type: Object,
      default: () => ({}),
    },
    filtersLoading: Boolean,
    eventId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      /** @type {import('rxjs').Observable} */
      subject$: this.getSubscription(),
      rootNode: new RootTreeNode({ id: 'root' }),
      /** @type {TreeNode[]} */
      roadmapNodes: [],
    }
  },
  computed: {
    ...lkMapper.mapGetters(['profile']),
    hasEmptyFilterMessage() {
      return (
        this.rootNode.children.length === 0
        && !this.isLoading
        && Object.values(this.filters || {}).filter(Boolean).length > 0
      )
    },
    emptyFilterMessage() {
      const es = this.filters.expert_status
      if (es) {
        return `Отсутствуют Меры в статусе "${getRoadmapEventExpertStatusesLabel(es)}"`
      }
      return 'Карты не найдены'
    },
  },
  watch: {
    filters() {
      return this.onInit().finally(this.onInitWS)
    },
    filtersLoading: {
      immediate: true,
      // eslint-disable-next-line consistent-return
      handler(v) {
        if (!v) {
          return this.onInit().finally(this.onInitWS)
        }
      },
    },
    eventId(v) {
      if (!this.isLoading) {
        this.onExpandNodes(this.rootNode, v)
      }
    },
    roadmapNodes(nodes, oldNodes) {
      const newNodes = difference(nodes, oldNodes)
      this.addWSRoadmapTasks(newNodes)
    },
  },
  activated() {
    // После mounted срабатывает activated почему-то, так что защитимся
    if (this.cancelTokenSource) return null

    return this.onInit()
      .finally(this.onInitWS)
  },
  deactivated() {
    this.cancelTokenSource?.cancel()
    this.cancelTokenSource = null

    this.taskQuery?.destroy()
    this.taskQuery = null
  },
  beforeDestroy() {
    this.cancelTokenSource?.cancel()
    this.cancelTokenSource = null

    this.taskQuery?.destroy()
    this.taskQuery = null

    this.cleanupCache()
  },
  methods: {
    async onInit() {
      this.isLoading = true
      this.cancelTokenSource?.cancel()
      this.cancelTokenSource = axios.CancelToken.source()
      const cancelToken = this.cancelTokenSource.token

      /**
       * 1 -----------------------------------------------
       * 8 регионов -> ... больше не запрашиваем
       * [ cписок регионов ] - в закрытом виде
       *
       * 2 -----------------------------------------------
       * 1 регион -> 8 муниципалитет -> ... больше не запрашиваем
       * [ cписок муниципалитет ] - в закрытом виде
       *
       * 3 -----------------------------------------------
       * 1 регион -> 1 муниципалитет -> 8 ОО -> список ДК по каждой ОО
       * [ cписок ОО ][ список ДК ] - открыты
       *
       * 4 -----------------------------------------------
       * 1 регион -> 1 муниципалитет -> 1 ОО -> список ДК
       * [ список ДК ] - открыты
       */

      let maxLevel
      let currentNode
      const options = { name: 'Дерево ATE', id: 'root' }
      const rootNode = new RootTreeNode(options)
      this.rootNode = rootNode

      // 1 -----------------------------------------------
      await this.onLoadChildren(rootNode, false, cancelToken)
      currentNode = rootNode
      maxLevel = LEVELS.ROOT

      // 2 -----------------------------------------------
      if (currentNode.children.length === 1) {
        const [municipalityNode] = currentNode.children
        await this.onLoadChildren(municipalityNode, false, cancelToken)
        currentNode = municipalityNode
      }

      // 3 -----------------------------------------------
      if (currentNode.children.length === 1) {
        const [schoolNode] = currentNode.children
        await this.onLoadChildren(schoolNode, false, cancelToken)
        currentNode = schoolNode
      }

      // 4 -----------------------------------------------
      if (currentNode.children.length === 1) {
        const [roadmapNode] = currentNode.children
        await this.onLoadChildren(roadmapNode, false, cancelToken)
        maxLevel = LEVELS.DIRECTIONS
      }

      if (maxLevel) {
        const promises = currentNode.children
          .map((node) => this.updateTreeChildren(node, maxLevel))
        await Promise.all(promises)
      }

      this.rootNode = currentNode

      if (this.eventId != null) {
        await this.onExpandNodes(this.rootNode, this.eventId)
      }

      this.cancelTokenSource = null
      this.isLoading = false
    },

    async onInitWS() {
      if (this.taskQuery) return
      this.taskQuery = new BaseTaskQuery()

      const taskUnread = {
        type: TaskTypes.eventUnread,
        callback: this.onWSEventUnread,
      }

      const taskStatus = {
        type: TaskTypes.eventStatusChanged,
        callback: this.onWSEventStatusChanged,
      }

      this.taskQuery.subscribe(taskUnread)
      this.taskQuery.subscribe(taskStatus)

      // TODO: на самом деле нужно подгрузить все isActive узлы дерева
      // Обновим данные по всем загруженным и видимым roadmaps
      const promises = this.roadmapNodes
        .filter((node) => node.parent.isActive)
        .map(this.updateRoadmap)

      await Promise.allSettled(promises)
      this.addWSRoadmapTasks(this.roadmapNodes)
    },

    /** @param {TreeNode} node */
    async onLoadChildren(node, isActive = false, cancelToken) {
      const promise = new Promise((resolve, reject) => {
        const callback = () => this.loadChildren(node, isActive, cancelToken)
          .then(resolve).catch(reject)
        this.subject$.next(callback)
      })

      try {
        node.loading = true
        await promise
        return null
      } catch (e) {
        setTimeout(this.cleanupCache)

        if (!axios.isCancel(e)) {
          this.$notice.errors(e)
        }

        return Promise.reject(e)
      } finally {
        node.loading = false
      }
    },

    /** @param {TreeNode} node */
    async loadChildren(node, isActive = false, cancelToken) {
      const filters = omitBy(this.filters, isEmptyCustom)
      const { id } = node.data

      switch (node.level) {
        case LEVELS.ROOT: {
          const data = await this.getRegions(filters, { cancelToken })
          node.setChildren(data, false, isActive)
          node.isLast = data.length ? false : IS_LAST
          break
        }

        case LEVELS.REGIONS: {
          const data = await this.getMunicipalities(id, filters, { cancelToken })
          node.setChildren(data, false, isActive)
          node.isLast = data.length ? false : IS_LAST
          break
        }

        case LEVELS.MUNICIPALITIES: {
          const data = await this.getSchools(id, filters, { cancelToken })
          node.setChildren(data, false, isActive)
          node.isLast = data.length ? false : IS_LAST
          break
        }

        case LEVELS.SCHOOLS: {
          const data = await this.getRoadmaps(id, filters, { cancelToken })
          node.setChildren(data, false, isActive)
          node.isLast = data.length ? false : IS_LAST
          this.roadmapNodes = uniq(this.roadmapNodes.concat(node.children))
          break
        }

        case LEVELS.ROADMAPS: {
          const data = node.data.directions || []
          node.setChildren(data, false, isActive)
          break
        }

        case LEVELS.DIRECTIONS: {
          const data = node.data.events || []
          node.setChildren(data, false, isActive)
          break
        }

        case LEVELS.EVENTS: {
          const data = node.data.files || []
          node.setChildren(data, false, isActive)
          break
        }

        default: {
          const message = `[${this.$options.name}]: Уровень "${node.level}" не поддерживается`
          // eslint-disable-next-line no-console
          console.warn(message)
        }
      }
    },

    getRegions: memoize(async (filters, options) => {
      const api = new RoadmapsFeedApi()
      const params = { perSize: PER_PAGE, ...filters }

      const { data } = await api.getRegions(params, options)
      return data
    }, resolverFilters),
    getMunicipalities: memoize(async (id, filters, options) => {
      const api = new RoadmapsFeedApi()
      const params = { perSize: PER_PAGE, region_id: id, ...filters }

      const { data } = await api.getMunicipalities(params, options)
      return data
    }, resolverFilters),
    getSchools: memoize(async (id, filters, options) => {
      const api = new RoadmapsFeedApi()
      const params = { perSize: PER_PAGE, municipality_id: id, ...filters }

      const { data } = await api.getSchools(params, options)
      return data
    }, resolverFilters),
    getRoadmaps: memoize(async (id, filters, options) => {
      const api = new RoadmapsFeedApi()
      const params = { perSize: PER_PAGE, school_id: id, ...filters }

      const { data } = await api.getRoadmaps(params, options)
      return data
    }, resolverFilters),

    cleanupCache() {
      Object.values(this.$options.methods)
        .forEach((method) => { method.cache?.clear() })
    },

    /** @param {TreeNode} node */
    async onChangeEventStatus(node, status, callback) {
      const roadmapEventsApi = new RoadmapEventsApi()
      const action = this.isAdresat
        ? roadmapEventsApi.updateStatus
        : roadmapEventsApi.updateCuratorStatus

      const unwatch = callback?.()
      const eventId = node.data.id

      try {
        await action.call(roadmapEventsApi, eventId, status)
        node.data.status = status
      } catch (e) {
        this.$notice.errors(e)
      } finally {
        unwatch?.()
      }
    },

    async onChangeExpertStatus(node, expertStatus, callback) {
      const roadmapEventsApi = new LkRoadmapExpertsFormApi()
      let action
      let userId = null
      switch (expertStatus) {
        case RoadmapEventExpertStatuses.inProgress:
          userId = this.profile.id
          action = roadmapEventsApi.expertise
          break
        case RoadmapEventExpertStatuses.needsReview:
          action = roadmapEventsApi.needExpertise
          break
        case RoadmapEventExpertStatuses.done:
          userId = this.profile.id
          action = roadmapEventsApi.doneExpertise
          break
        default:
          return
      }

      const unwatch = callback?.()
      const eventId = node.data.id

      try {
        await action.call(roadmapEventsApi, eventId)
        node.data.expert_status = expertStatus
        node.data.expert_user_id = userId
      } catch (e) {
        this.$notice.errors(e)
      } finally {
        unwatch?.()
      }
    },

    /** @param {TreeNode} node */
    async onChangeEventDate(node, date, callback) {
      const api = new RoadmapEventsApi()
      const unwatch = callback?.()

      const { data } = node
      const oldDate = data.end_at
      data.end_at = date

      try {
        await api.update(data.id, data)
      } catch (e) {
        node.data.end_at = oldDate
        this.$notice.errors(e)
      }

      unwatch?.()
    },

    /** @param {TreeNode} node */
    async updateRoadmap(node) {
      const api = new RoadmapsFeedApi()

      let roadmapNode = node
      while (roadmapNode.level > LEVELS.ROADMAPS) {
        roadmapNode = roadmapNode.parent
      }

      const roadmapId = roadmapNode.data.id
      const params = { school_id: roadmapNode.parent.data.id }

      try {
        const { data } = await api.getRoadmap(roadmapId, params)
        if (data) {
          roadmapNode.data = data
          this.updateRoadmapDataAndParentCounts(roadmapNode, data)
          await this.updateTreeChildren(roadmapNode)
        }
        return roadmapNode
      } catch (e) {
        this.$notice.errors(e)
        return null
      }
    },

    /** @param {TreeNode} roadmapNode */
    updateRoadmapDataAndParentCounts(roadmapNode, data) {
      const { region, municipality, school } = data
      const schoolNode = roadmapNode.parent
      if (!schoolNode) return
      schoolNode.data = Object.assign(schoolNode.data, { ...school })

      const municipalityNode = schoolNode.parent
      if (!municipalityNode) return
      municipalityNode.data = Object.assign(municipalityNode.data, { ...municipality })

      const regionNode = municipalityNode.parent
      if (!regionNode) return
      regionNode.data = Object.assign(regionNode.data, { ...region })
    },

    /** @param {TreeNode[]} node */
    onOpenEventHitory(node) {
      this.$emit('open-history', node)
      setTimeout(this.readEvent, TIMEOUT_READ, node)
    },

    /** @param {TreeNode[]} node */
    async readEvent(node) {
      // В апи запроса is_read может прилететь как undefuned, так что защитимся
      if (node.data.is_read !== false) return

      const api = new RoadmapEventsApi()

      try {
        await api.read(node.data.id)
        node.data.is_read = true
      } catch (e) {
        this.$notice.errors(e)
      }

      if (!node.data.is_read) return
      let currentNode = node.parent

      while (currentNode.parent) {
        currentNode.data.events_unread_count -= 1
        currentNode = currentNode.parent
      }
    },

    /** @param {TreeNode} node */
    async updateTreeChildren(node, maxLevel = 0) {
      const isActive = !!maxLevel && maxLevel > node.level
      if (isActive) node.isActive = isActive
      await this.onLoadChildren(node)

      if (!node.isActive) return

      const promises = node.children.map((nodeItem) => {
        if (node.isActive) {
          return this.updateTreeChildren(nodeItem, maxLevel)
        }
        return null
      })

      await Promise.all(promises)
    },

    /** @param {TreeNode[]} roadmapNodes */
    addWSRoadmapTasks(roadmapNodes = []) {
      if (!this.taskQuery) return

      roadmapNodes.forEach((node) => {
        const task = {
          type: TaskTypes.roadmap,
          object_id: node.data.id,
          params: {
            school_id: node.parent.data.id,
          },
          callback: (data) => this.onWSEventRoadmap(node, data),
        }

        this.taskQuery.subscribe(task)
      })
    },

    /** @param {WSEventStatusChanged} event */
    onWSEventStatusChanged(event) {
      const idNames = [
        'region_id',
        'municipality_id',
        'school_id',
        // еще прилетает roadmap ws event, так что 2 раза менять  не будем
        // 'roadmap_id',
        // 'direction_id',
        // 'event_id',
      ]

      // like in_progress -> in_progress_count
      const oldStatusPropName = STATUS_COUNT_MAP[event.data.old_status]
      const newStatusPropName = STATUS_COUNT_MAP[event.data.new_status]

      let node = this.rootNode.root
      let idName = idNames.shift()
      let id = event.params[idName]

      while (node && id) {
        // eslint-disable-next-line no-loop-func
        node = node.children.find((_) => _.data.id === id)
        if (!node) break

        if (oldStatusPropName in node.data) {
          node.data[oldStatusPropName] -= 1
        }

        if (newStatusPropName in node.data) {
          node.data[newStatusPropName] += 1
        }

        idName = idNames.shift()
        id = event.params[idName]
      }
    },

    /** @param {WSEventUnread} event */
    async onWSEventUnread(event) {
      const idNames = [
        'region_id',
        'municipality_id',
        'school_id',
        'roadmap_id',
        'direction_id',
        'event_id',
      ]

      let node = this.rootNode.root
      let idName = idNames.shift()
      let id = event.params[idName]

      while (node && id) {
        // eslint-disable-next-line no-loop-func
        node = node.children.find((_) => _.data.id === id)
        if (!node) break

        switch (idName) {
          case 'event_id':
            node.data.is_read = false
            break

          case 'roadmap_id':
          case 'direction_id':
            // eslint-disable-next-line no-await-in-loop
            await this.onLoadChildren(node)

          // eslint-disable-next-line no-fallthrough
          default:
            node.data.events_unread_count += 1
        }

        idName = idNames.shift()
        id = event.params[idName]
      }
    },

    /** @param {TreeNode} node; @param {WSRoadmap|WSRoadmapEvent} event */
    async onWSEventRoadmap(node, event) {
      if (node.parent.data.id !== event.params.school_id) {
        // в подписке на свойства из params не смотрим, так что здесь защита
        return
      }

      if (event.subtype === 'event') {
        await this.onLoadChildren(node)

        let nodeEvent
        const eventId = event.params.event_id

        for (let i = 0; i < node.children.length; i += 1) {
          const directionNode = node.children[i]
          // eslint-disable-next-line no-await-in-loop
          await this.onLoadChildren(directionNode)
          nodeEvent = node.children.find((_) => _.data.id === eventId)
          if (nodeEvent) break
        }

        if (!nodeEvent) return
        // нужно мутировать данные
        Object.keys(event.message).forEach((key) => {
          if (!(key in nodeEvent.data)) return
          nodeEvent.data[key] = event.message[key]
        })
      } else {
        node.data = event.message
        this.updateRoadmapDataAndParentCounts(node, event.message)
        this.updateTreeChildren(node)
      }
    },

    getSubscription() {
      // https://bit.ly/34hSCs8 - gitconnected.com
      // https://bit.ly/3mfqADX - stackblitz.com
      // https://bit.ly/3ohRAVd - medium.com
      const subject$ = new Subject()
        .pipe(flatMap(
          (callback) => defer(callback).pipe(retryWhen(
            (errors) => errors.pipe(
              delay(CONCURRENT_RETRY_TIMEOUT),
              take(CONCURRENT_RETRY_COUNT),
            ),
          )),
          null,
          CONCURRENT_COUNT,
        ))

      const subscription = subject$.subscribe()

      this.$once('hook:beforeDestroy', () => {
        subscription.unsubscribe()
      })

      return subject$
    },

    async onExpandNodes(node, targetNodeId) {
      if (node.level > LEVELS.EVENTS) {
        return false
      }


      if (node.data.id === +targetNodeId) {
        node.isActive = true
        let { parent } = node
        while (parent) {
          parent.isActive = true
          parent = parent.parent
        }

        setTimeout(() => {
          document.getElementById(`node.event.${node.data.id}`)?.scrollIntoView()
        }, 1250)
        return true
      }

      await this.onLoadChildren(node)
      // eslint-disable-next-line no-restricted-syntax
      for (const child of node.children) {
        // eslint-disable-next-line no-await-in-loop
        if (await this.onExpandNodes(child, targetNodeId)) {
          return true
        }
      }
      return false
    },
  },
}
</script>
